import { useState, ReactNode } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { CustomTheme, useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { RiErrorWarningLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import { useAccount } from '@contexts/AccountProvider';
import getEnumTranslation from '@utils/getEnumTranslation';

export type LimitBarProps = {
    limitsLoading?: boolean;
    denominatorValue: number;
    remainingValue: number;
    primaryStackValue: number;
    title: ReactNode;
    secondaryStackValue?: number;
    secondaryStackLabel?: ReactNode;
    alert?: ReactNode;
    primaryStackIsError?: boolean;
    onClick?: () => void;
    valuesAsCurrency?: boolean;
};

const LimitBar = ({
    primaryStackValue,
    remainingValue,
    secondaryStackValue = 0,
    secondaryStackLabel,
    denominatorValue,
    limitsLoading = false,
    title,
    alert,
    onClick,
    primaryStackIsError = false,
    valuesAsCurrency = true,
}: LimitBarProps) => {
    const { program, isLoading } = useAccount();
    const { theme } = useTheme();
    const [isHovered, setIsHovered] = useState(false);

    const currencyCode = program?.currency_numeric_code;

    const clamp = (num: number, min: number, max: number) =>
        Math.min(Math.max(num, min), max);

    const primaryBarWidth = clamp(
        (primaryStackValue / denominatorValue) * 100,
        0,
        100,
    );
    const secondaryBarWidth = clamp(
        (secondaryStackValue / denominatorValue) * 100,
        0,
        100,
    );

    const leftZeroPosForSecondaryText = -50;
    const secondaryStackTextPosition = clamp(
        leftZeroPosForSecondaryText + primaryBarWidth,
        -30,
        30,
    );

    const currencyTextStyle = {
        color: theme?.custom?.brand?.textMain || 'text.primary',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: '1.5rem',
    };

    const primaryStackDisplayValue = valuesAsCurrency ? (
        <CurrencyFormatter amount={primaryStackValue} currency={currencyCode} />
    ) : (
        primaryStackValue
    );

    const secondaryStackDisplayValue = valuesAsCurrency ? (
        <CurrencyFormatter
            amount={secondaryStackValue}
            currency={currencyCode}
        />
    ) : (
        secondaryStackValue
    );

    const remainingDisplayValue = valuesAsCurrency ? (
        <CurrencyFormatter amount={remainingValue} currency={currencyCode} />
    ) : (
        remainingValue
    );

    const denominatorDisplayValue = valuesAsCurrency ? (
        <CurrencyFormatter amount={denominatorValue} currency={currencyCode} />
    ) : (
        denominatorValue
    );

    return (
        <Panel
            {...(!!onClick && {
                onClick: onClick,
                isHovered: setIsHovered,
            })}>
            {!!onClick && (
                <PanelTitle
                    hovered={isHovered}
                    loading={isLoading}
                    arrowLabel={getEnumTranslation('viewAll')}>
                    <FormattedMessage
                        id="LimitBar.limits"
                        defaultMessage="Limits"
                    />
                </PanelTitle>
            )}
            <PanelContent>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                    <Typography variant="overline">{title}</Typography>
                    <Typography
                        variant="h2"
                        data-testid="denominator-value"
                        sx={(theme: CustomTheme) => ({
                            color:
                                theme?.custom?.brand?.textMain ||
                                'text.primary',
                            fontWeight: 600,
                        })}>
                        {isLoading || limitsLoading ? (
                            <Skeleton
                                width={theme.spacing(12.5)}
                                data-testid="denominator-value-loader"
                            />
                        ) : (
                            denominatorDisplayValue
                        )}
                    </Typography>
                    <Box
                        width="100%"
                        display="flex"
                        justifyContent="space-between"
                        marginTop={2.125}>
                        <Box>
                            <Typography variant="body2">
                                <FormattedMessage
                                    id="LimitBar.Used"
                                    defaultMessage="Used"
                                />
                            </Typography>
                            <Typography
                                sx={currencyTextStyle}
                                data-testid="primary-stack-value">
                                {isLoading || limitsLoading ? (
                                    <Skeleton
                                        width={theme.spacing(7.5)}
                                        data-testid="primary-stack-value-loader"
                                    />
                                ) : (
                                    primaryStackDisplayValue
                                )}
                            </Typography>
                        </Box>
                        {secondaryStackValue > 0 && (
                            <Box
                                position="relative"
                                left={`${secondaryStackTextPosition}%`}>
                                <Typography variant="body2" position="relative">
                                    {secondaryStackLabel}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    data-testid="secondary-stack-value"
                                    position="relative"
                                    sx={currencyTextStyle}>
                                    {secondaryStackDisplayValue}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="body2" position="relative">
                                <FormattedMessage
                                    id="LimitBar.Available"
                                    defaultMessage="Available"
                                />
                            </Typography>
                            <Typography
                                position="relative"
                                sx={currencyTextStyle}
                                data-testid="remaining-value">
                                {isLoading || limitsLoading ? (
                                    <Skeleton
                                        width={theme.spacing(7.5)}
                                        data-testid="remaining-value-loader"
                                    />
                                ) : (
                                    remainingDisplayValue
                                )}
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={(theme) => ({
                            display: 'inline-flex',
                            mr: 2.5,
                            mb: 2,
                            mt: 0.625,
                            height: '4px',
                            width: '100%',
                            position: 'relative',
                            background: theme.palette.grey['200'],
                        })}>
                        <Box
                            data-testid="primary-stack-bar"
                            sx={(theme) => ({
                                width: `${primaryBarWidth}%`,
                                display: 'inline-block',
                                height: '100%',
                                borderTopLeftRadius: theme.shape.borderRadius,
                                borderBottomLeftRadius:
                                    theme.shape.borderRadius,
                                backgroundColor: primaryStackIsError
                                    ? 'error.main'
                                    : 'primary.main',
                                position: 'relative',
                                overflow: 'hidden',
                            })}
                        />
                        <Box
                            data-testid="secondary-stack-bar"
                            sx={(theme) => ({
                                width: `${secondaryBarWidth}%`,
                                display: 'inline-block',
                                height: '100%',
                                borderTopRightRadius: theme.shape.borderRadius,
                                borderBottomRightRadius:
                                    theme.shape.borderRadius,
                                backgroundColor: 'info.main',
                                position: 'relative',
                                overflow: 'hidden',
                            })}
                        />
                    </Box>
                    {!!alert && (
                        <Box display="flex" color="error.main">
                            <Box display="flex" alignItems="center" mr={1}>
                                <RiErrorWarningLine />
                            </Box>
                            <Typography
                                component="span"
                                color="error"
                                variant="overline"
                                sx={{ fontWeight: 600 }}>
                                {alert}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </PanelContent>
        </Panel>
    );
};

export default LimitBar;
